import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Download, Loader2 } from "lucide-react";
import { CloseButton } from "../BackgroundSelectionModal/index.styled";
import BackgroundSelectionModal from "../BackgroundSelectionModal";
import DraggableQuestionOverlay, {
  type OverlayPosition,
} from "../DraggableQuestionOverlay";
import QuestionPreviewBox from "../../shared/QuestionPreviewBox";
import { useMediaRecorder } from "../../../hooks/useMediaRecorder";
import { useScrollLock } from "../../../hooks/useScrollLock";
import { runBackgroundExport } from "../../../utils/backgroundMediaExport";
import { isMobileDevice } from "../../../utils/deviceUtils";
import {
  exportVideoWithOverlay,
  MOBILE_VIDEO_CONSTRAINTS,
  renderOverlayElementToImage,
} from "../../../utils/mediaExportUtils";
import { warmupFfmpeg } from "../../../utils/videoTranscodeUtils";
import { hasVideoLetterbox } from "../../../utils/videoLayoutUtils";
import { BACKGROUNDS } from "../../../constants/backgrounds";
import { showToast } from "../../../utils/toastUtils";
import {
  MediaAnswerModalContainer,
  MediaAnswerModalOverlay,
  VideoPreviewStage,
} from "../shared/MediaAnswerModal.styled";

interface VideoAnswerModalProps {
  isOpen: boolean;
  onClose: () => void;
  boxTitle: string;
  questionText: string;
  footerText?: string;
  exportId: string;
  onDownloaded?: () => void;
}

type SourceMode = "choose" | "record" | "upload";

const DEFAULT_POSITION: OverlayPosition = { x: 0, y: 48 };

const ERROR_MESSAGES = {
  permission_denied: "נדרשת הרשאת מצלמה ומיקרופון כדי להקליט",
  not_supported: "הדפדפן לא תומך בהקלטת וידאו",
  unknown: "שגיאה בהקלטה, נסו שוב",
};

const VideoAnswerModal = ({
  isOpen,
  onClose,
  boxTitle,
  questionText,
  footerText,
  exportId,
  onDownloaded,
}: VideoAnswerModalProps) => {
  const stageRef = useRef<HTMLDivElement>(null);
  const exportOverlayRef = useRef<HTMLDivElement>(null);
  const answerOverlayRef = useRef<HTMLDivElement>(null);
  const liveVideoRef = useRef<HTMLVideoElement>(null);
  const playbackVideoRef = useRef<HTMLVideoElement>(null);
  const previewStreamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraCaptureInputRef = useRef<HTMLInputElement>(null);
  const playbackUrlRef = useRef<string | null>(null);

  const [position, setPosition] = useState<OverlayPosition>(DEFAULT_POSITION);
  const [sourceMode, setSourceMode] = useState<SourceMode>("choose");
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [isMirrored, setIsMirrored] = useState(true);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isMobile] = useState(isMobileDevice);
  const [hasLetterbox, setHasLetterbox] = useState(false);
  const [selectedBackground, setSelectedBackground] = useState(BACKGROUNDS[0]);
  const [isBackgroundModalOpen, setIsBackgroundModalOpen] = useState(false);

  useScrollLock(isOpen);

  const {
    isRecording,
    recordedBlob,
    error: recorderError,
    recordExistingStream,
    stopRecording,
    reset,
  } = useMediaRecorder();

  const revokePlaybackUrl = useCallback(() => {
    if (playbackUrlRef.current) {
      URL.revokeObjectURL(playbackUrlRef.current);
      playbackUrlRef.current = null;
    }
  }, []);

  const stopPreviewStream = useCallback(() => {
    previewStreamRef.current?.getTracks().forEach((t) => t.stop());
    previewStreamRef.current = null;
  }, []);

  const resetModalState = useCallback(() => {
    stopRecording();
    stopPreviewStream();
    reset();
    revokePlaybackUrl();
    setSourceMode("choose");
    setVideoBlob(null);
    setIsMirrored(true);
    setIsPreviewMode(false);
    setCameraError(null);
    setPosition(DEFAULT_POSITION);
    setHasLetterbox(false);
    setSelectedBackground(BACKGROUNDS[0]);
    setIsBackgroundModalOpen(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraCaptureInputRef.current) cameraCaptureInputRef.current.value = "";
  }, [reset, revokePlaybackUrl, stopPreviewStream, stopRecording]);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    stopPreviewStream();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: MOBILE_VIDEO_CONSTRAINTS,
        audio: true,
      });
      previewStreamRef.current = stream;
      if (liveVideoRef.current) {
        liveVideoRef.current.srcObject = stream;
        await liveVideoRef.current.play();
      }
    } catch {
      setCameraError(ERROR_MESSAGES.permission_denied);
    }
  }, [stopPreviewStream]);

  const handleClose = useCallback(() => {
    resetModalState();
    onClose();
  }, [onClose, resetModalState]);

  useEffect(() => {
    if (!isOpen) return;
    warmupFfmpeg();
    resetModalState();
    const frame = requestAnimationFrame(() => {
      const stage = stageRef.current;
      setPosition(
        stage
          ? { x: 0, y: Math.round(stage.clientHeight * 0.075) }
          : DEFAULT_POSITION,
      );
    });
    return () => {
      cancelAnimationFrame(frame);
      stopPreviewStream();
      reset();
    };
  }, [isOpen, reset, resetModalState, stopPreviewStream]);

  useEffect(() => {
    if (!isPreviewMode || !recordedBlob || sourceMode !== "record") return;
    setVideoBlob(recordedBlob);
  }, [isPreviewMode, recordedBlob, sourceMode]);

  useEffect(() => {
    if (!isPreviewMode || !videoBlob) return;

    const video = playbackVideoRef.current;
    if (!video) return;

    revokePlaybackUrl();

    const url = URL.createObjectURL(videoBlob);
    playbackUrlRef.current = url;
    video.src = url;
    void video.play();

    return () => {
      video.pause();
      video.removeAttribute("src");
      video.load();
      if (playbackUrlRef.current === url) {
        URL.revokeObjectURL(url);
        playbackUrlRef.current = null;
      }
    };
  }, [isPreviewMode, revokePlaybackUrl, videoBlob]);

  const updateLetterboxFromVideo = useCallback(
    (videoWidth: number, videoHeight: number) => {
      const stage = stageRef.current;
      if (!stage) {
        setHasLetterbox(false);
        return;
      }
      setHasLetterbox(
        hasVideoLetterbox(
          videoWidth,
          videoHeight,
          stage.clientWidth,
          stage.clientHeight,
        ),
      );
    },
    [],
  );

  useEffect(() => {
    if (!isPreviewMode || !videoBlob) {
      setHasLetterbox(false);
      return;
    }

    const video = playbackVideoRef.current;
    if (!video) return;

    const handleMetadata = () => {
      updateLetterboxFromVideo(video.videoWidth, video.videoHeight);
    };

    if (video.readyState >= 1) {
      handleMetadata();
    } else {
      video.addEventListener("loadedmetadata", handleMetadata);
      return () => video.removeEventListener("loadedmetadata", handleMetadata);
    }
  }, [isPreviewMode, updateLetterboxFromVideo, videoBlob]);

  const handleChooseRecord = () => {
    setSourceMode("record");
    setIsMirrored(true);
    setCameraError(null);
    void startCamera();
  };

  const handleChooseUpload = () => {
    fileInputRef.current?.click();
  };

  const handleChooseNativeCamera = () => {
    cameraCaptureInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";

    if (!file || file.size === 0) {
      if (file) showToast("לא ניתן לטעון את הווידאו");
      return;
    }

    if (file.type && !file.type.startsWith("video/")) {
      showToast("לא ניתן לטעון את הווידאו");
      return;
    }

    setVideoBlob(file);
    setSourceMode("upload");
    setIsMirrored(false);
    setIsPreviewMode(true);
  };

  const handleStartRecording = () => {
    if (!previewStreamRef.current) return;
    recordExistingStream(previewStreamRef.current, true);
  };

  const handleStopRecording = () => {
    stopRecording();
    stopPreviewStream();
    if (liveVideoRef.current) liveVideoRef.current.srcObject = null;
    setIsPreviewMode(true);
  };

  const handleRetake = () => {
    if (sourceMode === "upload") {
      if (isMobile) {
        cameraCaptureInputRef.current?.click();
      } else {
        fileInputRef.current?.click();
      }
      return;
    }

    revokePlaybackUrl();
    setVideoBlob(null);
    setIsPreviewMode(false);
    reset({ keepStream: false });
    void startCamera();
  };

  const handleDownload = async () => {
    if (!videoBlob || !exportOverlayRef.current || !stageRef.current) return;
    if (videoBlob.size === 0) {
      showToast("ההקלטה ריקה, נסו להקליט שוב");
      return;
    }

    playbackVideoRef.current?.pause();

    const stage = stageRef.current;
    const cardWidth =
      answerOverlayRef.current?.offsetWidth ?? Math.max(1, stage.clientWidth - 12);
    exportOverlayRef.current.style.width = `${cardWidth}px`;

    setIsPreparing(true);
    try {
      const overlayImg = await renderOverlayElementToImage(exportOverlayRef.current);
      const exportBlob = new Blob([await videoBlob.arrayBuffer()], {
        type: videoBlob.type,
      });

      await runBackgroundExport(
        () =>
          exportVideoWithOverlay({
            videoBlob: exportBlob,
            overlayImg,
            overlayPosition: position,
            previewWidth: stage.clientWidth,
            previewHeight: stage.clientHeight,
            mirrorVideo: isMirrored,
            background: hasLetterbox ? selectedBackground : undefined,
            filename: `video-answer-${exportId}`,
          }),
        {
          success: "הסרטון הורד בהצלחה!",
          error: "שגיאה בהורדת הווידאו",
        },
      );
      onDownloaded?.();
    } catch (e) {
      console.error(e);
      showToast("שגיאה בהורדת הווידאו", "error");
    } finally {
      setIsPreparing(false);
    }
  };

  if (!isOpen) return null;

  const displayError =
    sourceMode === "record"
      ? cameraError ?? (recorderError ? ERROR_MESSAGES[recorderError] : null)
      : null;

  const overlayContent = (
    <QuestionPreviewBox
      title={boxTitle}
      footerText={footerText?.trim() ? footerText : undefined}
    >
      <span style={{ whiteSpace: "pre-wrap" }}>{questionText}</span>
    </QuestionPreviewBox>
  );

  const previewBackground = hasLetterbox ? selectedBackground : undefined;

  const modal = (
    <MediaAnswerModalOverlay onClick={handleClose}>
      <MediaAnswerModalContainer
        role="dialog"
        aria-modal="true"
        $compactPreview={isPreviewMode && hasLetterbox}
        onClick={(e) => e.stopPropagation()}
      >
        <CloseButton onClick={handleClose} aria-label="סגור">
          <X size={17} />
        </CloseButton>

        <h3 className="modal-title">סלפי וידאו תשובה</h3>

        <input
          ref={fileInputRef}
          type="file"
          accept="video/*"
          hidden
          onChange={handleFileChange}
        />
        <input
          ref={cameraCaptureInputRef}
          type="file"
          accept="video/*"
          capture="user"
          hidden
          onChange={handleFileChange}
        />

        <VideoPreviewStage
          ref={stageRef}
          className="preview-stage"
          $background={previewBackground}
        >
          {isPreviewMode ? (
            <video
              ref={playbackVideoRef}
              className={`playback-video${isMirrored ? " mirrored" : ""}`}
              playsInline
              controls={false}
              loop
            />
          ) : sourceMode === "record" ? (
            <video
              ref={liveVideoRef}
              className="camera-video mirrored"
              playsInline
              muted
              autoPlay
            />
          ) : null}

          <DraggableQuestionOverlay
            position={position}
            onPositionChange={setPosition}
            containerRef={stageRef}
            disabled={isRecording}
            axis="vertical"
          >
            <div ref={answerOverlayRef} style={{ width: "100%" }}>
              {overlayContent}
            </div>
          </DraggableQuestionOverlay>
        </VideoPreviewStage>

        {displayError && <p className="error-message">{displayError}</p>}

        <div className={`actions-row${isPreviewMode && hasLetterbox ? " post-recording" : ""}`}>
          {sourceMode === "choose" ? (
            isMobile ? (
              <>
                <button
                  type="button"
                  className="action-btn secondary"
                  onClick={handleChooseUpload}
                >
                  העלאת וידאו
                </button>
                <button
                  type="button"
                  className="action-btn"
                  onClick={handleChooseNativeCamera}
                >
                  צילום מהמצלמה
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="action-btn secondary"
                  onClick={handleChooseUpload}
                >
                  העלאת וידאו
                </button>
                <button
                  type="button"
                  className="action-btn"
                  onClick={handleChooseRecord}
                >
                  הקלטה מהמצלמה
                </button>
              </>
            )
          ) : isPreviewMode ? (
            <>
              {hasLetterbox ? (
                <div className="secondary-pair">
                  <button
                    type="button"
                    className="action-btn secondary"
                    onClick={() => setIsBackgroundModalOpen(true)}
                    disabled={isPreparing}
                  >
                    בחירת רקע
                  </button>
                  <button
                    type="button"
                    className="action-btn secondary"
                    onClick={handleRetake}
                    disabled={isPreparing}
                  >
                    {sourceMode === "upload" ? "בחירת וידאו אחר" : "הקלטה מחדש"}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="action-btn secondary"
                  onClick={handleRetake}
                  disabled={isPreparing}
                >
                  {sourceMode === "upload" ? "בחירת וידאו אחר" : "הקלטה מחדש"}
                </button>
              )}
              <button
                type="button"
                className="action-btn"
                onClick={handleDownload}
                disabled={isPreparing}
              >
                {isPreparing ? (
                  <Loader2 size={14} className="loading-icon" />
                ) : (
                  <Download size={14} />
                )}
                הורדה
              </button>
            </>
          ) : !isRecording ? (
            <button
              type="button"
              className="action-btn danger"
              onClick={handleStartRecording}
              disabled={!!cameraError}
            >
              התחל הקלטה
            </button>
          ) : (
            <button
              type="button"
              className="action-btn danger"
              onClick={handleStopRecording}
            >
              עצור
            </button>
          )}
        </div>

        <div ref={exportOverlayRef} className="hidden-export-overlay">
          {overlayContent}
        </div>
      </MediaAnswerModalContainer>

      <BackgroundSelectionModal
        isOpen={isBackgroundModalOpen}
        onClose={() => setIsBackgroundModalOpen(false)}
        mode="select"
        title="בחירת רקע"
        initialBackground={selectedBackground}
        onBackgroundSelected={setSelectedBackground}
      />
    </MediaAnswerModalOverlay>
  );

  return createPortal(modal, document.body);
};

export default VideoAnswerModal;
